// Minimal STORED-mode ZIP writer for the 3MF exporter.
//
// 3MF is a ZIP container with a small XML payload — DEFLATE compression is
// optional per spec (STORED works). PrusaSlicer / Bambu Studio / Cura / OrcaSlicer
// all accept STORED-only 3MFs, so we skip pulling in an inflate library.
//
// Format: each file gets a Local File Header + raw bytes. The Central Directory
// at the end indexes them, and the End-of-Central-Directory record points the
// reader to the directory. CRC-32 (PKZIP polynomial) is computed per entry —
// table-based for speed.

const CRC_TABLE = ((): Uint32Array => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
    t[i] = c >>> 0;
  }
  return t;
})();

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

interface ZipEntry {
  name: string;          // posix path inside zip
  data: Uint8Array;      // raw bytes (already encoded if text)
  crc: number;
  offset: number;        // local header offset, set during write
}

export class ZipBuilder {
  private entries: ZipEntry[] = [];

  /** Add a file. `data` may be a string (UTF-8 encoded) or Uint8Array. */
  add(name: string, data: string | Uint8Array): void {
    const bytes = typeof data === 'string' ? new TextEncoder().encode(data) : data;
    this.entries.push({ name, data: bytes, crc: crc32(bytes), offset: 0 });
  }

  /** Build the final zip buffer. */
  build(): Uint8Array {
    // Pass 1: compute total size to allocate buffer in one shot.
    let lhSize = 0;
    let cdSize = 0;
    for (const e of this.entries) {
      const nameBytes = new TextEncoder().encode(e.name);
      lhSize += 30 + nameBytes.length + e.data.length;
      cdSize += 46 + nameBytes.length;
    }
    const total = lhSize + cdSize + 22;
    const buf = new Uint8Array(total);
    const dv = new DataView(buf.buffer);
    let p = 0;

    // Local File Headers + file data
    for (const e of this.entries) {
      e.offset = p;
      const nameBytes = new TextEncoder().encode(e.name);
      dv.setUint32(p, 0x04034b50, true); p += 4;       // signature
      dv.setUint16(p, 20, true); p += 2;               // version needed
      dv.setUint16(p, 0x0800, true); p += 2;           // flags (UTF-8 filename bit)
      dv.setUint16(p, 0, true); p += 2;                // method = STORED
      dv.setUint16(p, 0, true); p += 2;                // mtime
      dv.setUint16(p, 0x21, true); p += 2;             // mdate (1980-01-01)
      dv.setUint32(p, e.crc, true); p += 4;
      dv.setUint32(p, e.data.length, true); p += 4;    // compressed size
      dv.setUint32(p, e.data.length, true); p += 4;    // uncompressed size
      dv.setUint16(p, nameBytes.length, true); p += 2;
      dv.setUint16(p, 0, true); p += 2;                // extra length
      buf.set(nameBytes, p); p += nameBytes.length;
      buf.set(e.data, p); p += e.data.length;
    }

    // Central Directory
    const cdStart = p;
    for (const e of this.entries) {
      const nameBytes = new TextEncoder().encode(e.name);
      dv.setUint32(p, 0x02014b50, true); p += 4;       // signature
      dv.setUint16(p, 20, true); p += 2;               // version made by
      dv.setUint16(p, 20, true); p += 2;               // version needed
      dv.setUint16(p, 0x0800, true); p += 2;           // flags
      dv.setUint16(p, 0, true); p += 2;                // method
      dv.setUint16(p, 0, true); p += 2;                // mtime
      dv.setUint16(p, 0x21, true); p += 2;             // mdate
      dv.setUint32(p, e.crc, true); p += 4;
      dv.setUint32(p, e.data.length, true); p += 4;
      dv.setUint32(p, e.data.length, true); p += 4;
      dv.setUint16(p, nameBytes.length, true); p += 2;
      dv.setUint16(p, 0, true); p += 2;                // extra
      dv.setUint16(p, 0, true); p += 2;                // comment
      dv.setUint16(p, 0, true); p += 2;                // disk #
      dv.setUint16(p, 0, true); p += 2;                // internal attrs
      dv.setUint32(p, 0, true); p += 4;                // external attrs
      dv.setUint32(p, e.offset, true); p += 4;
      buf.set(nameBytes, p); p += nameBytes.length;
    }
    const cdEnd = p;

    // End of Central Directory
    dv.setUint32(p, 0x06054b50, true); p += 4;
    dv.setUint16(p, 0, true); p += 2;                  // disk #
    dv.setUint16(p, 0, true); p += 2;                  // disk w/ start of CD
    dv.setUint16(p, this.entries.length, true); p += 2;
    dv.setUint16(p, this.entries.length, true); p += 2;
    dv.setUint32(p, cdEnd - cdStart, true); p += 4;    // CD size
    dv.setUint32(p, cdStart, true); p += 4;            // CD offset
    dv.setUint16(p, 0, true); p += 2;                  // comment length

    return buf;
  }
}
